import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Full-page fit/inseam education graphic with a self-contained zoom + pan
 * viewer (no external library). Works for every user, signed in or not.
 *
 * The image keeps its natural aspect ratio at all times — it's constrained by
 * max-width/max-height and centered (object-contain), so it is contained,
 * never stretched. Zoom is a CSS transform layered on top of that fitted size:
 *  - desktop: mouse wheel + on-screen +/- / reset buttons, drag to pan
 *  - mobile: two-finger pinch to zoom, one-finger drag to pan, double-tap toggle
 *
 * State lives in a single `view` object updated exclusively through functional
 * setState (so rapid inputs always compound from the latest value). A render
 * mirror (`viewRef`) lets native gesture handlers snapshot the starting value.
 * Pan is clamped to the scaled image's real overflow so it can't leave screen.
 */

const IMG_SRC = "/inseam-guide.png";
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const STEP = 1.4;

const InseamGuide = () => {
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  const imgRef = useRef(null);

  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  // Render mirror: gesture handlers read the current value at gesture start.
  const viewRef = useRef(view);
  viewRef.current = view;

  // Clamp scale to range and pan to the scaled image's real overflow.
  const clampView = useCallback((next) => {
    const vp = viewportRef.current;
    const img = imgRef.current;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next.scale));
    if (!vp || !img) return { scale, x: 0, y: 0 };
    const maxX = Math.max(0, (img.clientWidth * scale - vp.clientWidth) / 2);
    const maxY = Math.max(0, (img.clientHeight * scale - vp.clientHeight) / 2);
    return {
      scale,
      x: Math.min(maxX, Math.max(-maxX, next.x)),
      y: Math.min(maxY, Math.max(-maxY, next.y)),
    };
  }, []);

  const zoomIn = useCallback(
    () => setView((v) => clampView({ ...v, scale: v.scale * STEP })),
    [clampView]
  );
  const zoomOut = useCallback(
    () => setView((v) => clampView({ ...v, scale: v.scale / STEP })),
    [clampView]
  );
  const reset = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  // Native listeners (non-passive where we need preventDefault).
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return undefined;

    const onWheel = (e) => {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.0015);
      setView((v) => clampView({ ...v, scale: v.scale * factor }));
    };

    // Mouse / pen drag to pan.
    let drag = null;
    const onPointerDown = (e) => {
      if (e.pointerType === "touch") return;
      if (viewRef.current.scale <= 1) return;
      drag = { x: e.clientX, y: e.clientY, ox: viewRef.current.x, oy: viewRef.current.y };
      setDragging(true);
      try { vp.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onPointerMove = (e) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      setView((v) => clampView({ ...v, x: drag.ox + dx, y: drag.oy + dy }));
    };
    const onPointerUp = () => {
      drag = null;
      setDragging(false);
    };

    // Touch: pinch zoom + one-finger pan.
    let pinch = null;
    let touchDrag = null;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinch = { d: dist(e.touches), s: viewRef.current.scale };
        touchDrag = null;
      } else if (e.touches.length === 1 && viewRef.current.scale > 1) {
        const t = e.touches[0];
        touchDrag = { x: t.clientX, y: t.clientY, ox: viewRef.current.x, oy: viewRef.current.y };
      }
    };
    const onTouchMove = (e) => {
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        const ratio = dist(e.touches) / pinch.d;
        setView((v) => clampView({ ...v, scale: pinch.s * ratio }));
      } else if (touchDrag && e.touches.length === 1) {
        e.preventDefault();
        const t = e.touches[0];
        const dx = t.clientX - touchDrag.x;
        const dy = t.clientY - touchDrag.y;
        setView((v) => clampView({ ...v, x: touchDrag.ox + dx, y: touchDrag.oy + dy }));
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinch = null;
      if (e.touches.length === 0) touchDrag = null;
    };

    // Double-click / double-tap toggles fit <-> 2.5x.
    const onDblClick = () =>
      setView((v) => clampView(v.scale > 1 ? { scale: 1, x: 0, y: 0 } : { scale: 2.5, x: 0, y: 0 }));

    vp.addEventListener("wheel", onWheel, { passive: false });
    vp.addEventListener("pointerdown", onPointerDown);
    vp.addEventListener("pointermove", onPointerMove);
    vp.addEventListener("pointerup", onPointerUp);
    vp.addEventListener("pointercancel", onPointerUp);
    vp.addEventListener("touchstart", onTouchStart, { passive: false });
    vp.addEventListener("touchmove", onTouchMove, { passive: false });
    vp.addEventListener("touchend", onTouchEnd);
    vp.addEventListener("dblclick", onDblClick);

    return () => {
      vp.removeEventListener("wheel", onWheel);
      vp.removeEventListener("pointerdown", onPointerDown);
      vp.removeEventListener("pointermove", onPointerMove);
      vp.removeEventListener("pointerup", onPointerUp);
      vp.removeEventListener("pointercancel", onPointerUp);
      vp.removeEventListener("touchstart", onTouchStart);
      vp.removeEventListener("touchmove", onTouchMove);
      vp.removeEventListener("touchend", onTouchEnd);
      vp.removeEventListener("dblclick", onDblClick);
    };
  }, [clampView]);

  const atMin = view.scale <= MIN_SCALE + 0.001;
  const atMax = view.scale >= MAX_SCALE - 0.001;

  return (
    <div className="h-screen flex flex-col bg-[#FAF9F5]" data-testid="inseam-guide-page">
      <header className="flex-shrink-0 border-b border-[#E4E1D9] px-4 md:px-12 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="inseam-guide-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1
          className="text-2xl md:text-3xl tracking-tighter ml-1"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          Inseam Guide
        </h1>
      </header>

      <div
        ref={viewportRef}
        className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden select-none"
        style={{ touchAction: "none", cursor: view.scale > 1 ? (dragging ? "grabbing" : "grab") : "auto" }}
      >
        <img
          ref={imgRef}
          src={IMG_SRC}
          alt="How to measure your inseam and find the right jeans for your height"
          draggable={false}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            // A global reset applies transform-box:view-box to <img>, which can
            // nullify the transform — force border-box so zoom always applies.
            transformBox: "border-box",
            transformOrigin: "center center",
            transition: dragging ? "none" : "transform 0.18s ease-out",
            willChange: "transform",
          }}
          data-testid="inseam-guide-image"
        />

        {/* Zoom controls */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white/95 backdrop-blur border border-[#E4E1D9] rounded-full shadow-md px-1 py-1">
          <ControlButton onClick={zoomOut} label="Zoom out" disabled={atMin}>
            <Minus size={18} weight="bold" />
          </ControlButton>
          <ControlButton onClick={reset} label="Reset zoom" disabled={atMin}>
            <ArrowsClockwise size={16} weight="bold" />
          </ControlButton>
          <ControlButton onClick={zoomIn} label="Zoom in" disabled={atMax}>
            <Plus size={18} weight="bold" />
          </ControlButton>
        </div>

        <p
          className="absolute top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.15em] text-[#9A9890] pointer-events-none whitespace-nowrap"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          Pinch or scroll to zoom · drag to move
        </p>
      </div>
    </div>
  );
};

const ControlButton = ({ onClick, label, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="w-9 h-9 flex items-center justify-center rounded-full text-[#2D302A] hover:bg-[#F0EFEB] transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

export default InseamGuide;
